// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2024-05-16 16:10:41 +0200 (Thu, 16 May 2024) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 142859 $
//  Purpose   : image viewer layers
// =============================================================================
// ---Third-party imports
import React from 'react'
import * as ReactDOM from 'react-dom'

import clsx from 'clsx'
import {Rnd} from 'uix/chunks'

// ---Framework imports
import {Actions} from 'uix/registries'
import {get, flatten, mapItems, isArray, isNil, dict} from 'ldash'
import log from 'uix/logging'
import {useMemo, useSelector, useActions, useState, useResettableState, useEffect} from 'uix/hooks'

// ---Local imports
import {useWordData, useFulltextHits, useViewer} from './hooks'

import styles from './layers.scss'

// ---Globals
const translate_perc = (x1, y1, x2, y2, w_perc, h_perc) => {
  /* css style compatible percentage notation
   */
  x1 = x1 / w_perc
  y1 = y1 / h_perc
  return {
    left: `${x1}%`,
    top: `${y1}%`,
    width: `${x2 / w_perc - x1}%`,
    height: `${y2 / h_perc - y1}%`,
  }
}

const RndHighlight = ({
  x1,
  y1,
  x2,
  y2,
  text,
  label,
  w_perc,
  h_perc,
  className,
  onHover,
  onClick,
  onLayerInteract,
  z_fac,
  viewerId,
  page_otid,
  geomtype,
  itemId,
}) => {
  /* resizable and draggable geometry element
   */
  let setModifiedViewerGeometry = useActions(Actions.setModifiedViewerGeometry)

  // position is the top/left corner
  // reset if externally controlled
  let [changedPos, setChangedPos] = useResettableState(null, [x1, y1])
  let x = x1
  let y = y1
  if (changedPos) {
    ;[x, y] = changedPos
  }

  let w = x2 - x1
  let h = y2 - y1
  // reset if externally controlled
  let [changedWidth, setChangedWidth] = useResettableState(null, [w, h])
  if (changedWidth) {
    ;[w, h] = changedWidth
  }

  let onDragStop = (e, d) => {
    setChangedPos([parseInt(Math.abs(d.lastX * z_fac)), parseInt(Math.abs(d.lastY * z_fac))])
    onLayerInteract(false)
  }
  let onResizeStop = (e, direction, ref, delta) => {
    let delta_width = parseInt(delta.width * z_fac)
    let delta_height = parseInt(delta.height * z_fac)
    // probably not needed after react 18
    // but this event is not default batched
    ReactDOM.unstable_batchedUpdates(() => {
      setChangedWidth([w + delta_width, h + delta_height])
      // update position if left or top resizing
      setChangedPos([
        direction.endsWith('eft') ? x - delta_width : x,
        direction.startsWith('top') ? y - delta_height : y,
      ])
    })

    onLayerInteract(false)
  }

  useEffect(() => {
    if (changedPos || changedWidth) {
      let bbox = {x1: x, y1: y, x2: x + w, y2: y + h}
      setModifiedViewerGeometry(viewerId, page_otid, geomtype, {[itemId]: bbox})
    }
  }, [x, y, w, h])

  return (
    <Rnd
      bounds="parent"
      size={{width: w / z_fac, height: h / z_fac}}
      position={{x: x / z_fac, y: y / z_fac}}
      onDragStop={onDragStop}
      onDragStart={e => onLayerInteract(true)}
      onResizeStart={e => onLayerInteract(true)}
      onResizeStop={onResizeStop}
    >
      <div
        style={{width: '100%', height: '100%'}}
        title={text}
        className={className}
        onMouseEnter={onHover}
        onClick={onClick}
      >
        {!isNil(label) && <span className={styles.hl_label}>{label}</span>}
      </div>
    </Rnd>
  )
}

const highlight = ({
  x1,
  y1,
  x2,
  y2,
  text,
  label,
  w_perc,
  h_perc,
  key,
  as = 'div',
  className,
  onHover,
  onClick,
}) => {
  let bbox_perc = translate_perc(x1, y1, x2, y2, w_perc, h_perc)
  bbox_perc.position = 'absolute'

  className = className || styles.poly
  if (as == 'div') {
    return (
      <div
        key={key}
        style={bbox_perc}
        title={text}
        className={className}
        onMouseEnter={onHover}
        onClick={onClick}
      >
        {!isNil(label) && <span className={styles.hl_label}>{label}</span>}
      </div>
    )
  } else if (as == 'svg') {
    return (
      <svg
        key={key}
        style={bbox_perc}
        className={className}
        width={x2 - x1}
        height={y2 - y1}
        onMouseEnter={onHover}
        onClick={onClick}
      >
        <title>{text}</title>
      </svg>
    )
  } else {
    log.warn('invalid element name: %o', as)
    return null
  }
}

const get_perc = page => ({w_perc: page.size[0] / 100, h_perc: page.size[1] / 100})

const WordLayer = ({page}) => {
  if (!page.size) return null // corrupt page
  if (!page.ocr) return null

  let {w_perc, h_perc} = get_perc(page)

  //~ if (5890 === page.id) log('render')

  let wordData = useWordData(page.id)

  let words = useMemo(
    () =>
      get(wordData, 'words', []).map((word, i) => {
        // deskewed bounding box in org width/height
        let [x1, y1, x2, y2, text] = word
        return highlight({x1, y1, x2, y2, text, w_perc, h_perc, key: i, className: styles.word})
      }),
    [wordData]
  )

  if (!words) {
    return null
  }

  return <div className={clsx(styles.layer, styles.wordLayer)}>{words}</div>
}

const GeometryLayer = ({page, selectable, resizable, z_fac, onLayerInteract}) => {
  /* used for:
  - ocr-geometry (words, lines, paras, blocks)
  - entity-geometry
  */

  let viewer = useViewer()
  let setFocusedViewerGeometry = useActions(Actions.setFocusedViewerGeometry)
  let setSelectedViewerGeometry = useActions(Actions.setSelectedViewerGeometry)

  // use externally provided geometry for this vid/otid
  let geometry = useSelector(root => root.data.viewer.geometry[viewer.vid]?.[page.id])
  let focused_geom = useSelector(root => root.data.viewer.focusedGeometry[viewer.vid])

  // internally controlled selection (items need id attribute)
  let selectedGeometry = useSelector(
    root => root.data.viewer.selectedGeometry[viewer.vid]?.[page.id]
  )

  let focused_set = useMemo(() => {
    let focused = new Set()
    if (focused_geom && focused_geom.otid == page.id && focused_geom.geomtype == 'words') {
      let focused_index = focused_geom.index
      if (isArray(focused_index)) {
        focused = new Set(focused_index)
      } else if (!isNil(focused_index)) {
        focused = new Set([focused_index])
      }
    }
    return focused
  }, [focused_geom])

  let {w_perc, h_perc} = get_perc(page)
  let boxes = useMemo(
    () =>
      mapItems(geometry || [], (geomtype, items) =>
        items.map((item, i) => {
          // deskewed bounding box in org width/height
          let is_hidden = item.hidden
          let {x1, y1, x2, y2} = item.bbox
          let is_focused = geomtype == 'words' && focused_set.has(i)

          // use item className or generate from other item props
          let _className =
            item.className ||
            clsx(
              styles.poly,
              is_focused && styles.focused,
              is_hidden && styles.hidden,
              item.selected && styles.selected, // externally controlled
              selectable &&
                (selectedGeometry?.[geomtype] || []).includes(item.id) &&
                styles.selected, // internally controlled
              selectable && styles.selectable,
              item.deleted && styles.deleted,
              item.modified && styles.modified,
              styles[`geo_${geomtype}`]
            )

          let hl_props = {
            x1,
            y1,
            x2,
            y2,
            text: item.text,
            label: is_hidden ? null : item.label,
            w_perc,
            h_perc,
            key: `${geomtype}_${i}`,
            className: _className,
            onHover: item.hoverFocus
              ? () => setFocusedViewerGeometry(viewer.vid, page.id, geomtype, i)
              : null,
            // need a item.id for single click selection
            onClick:
              selectable && item.id
                ? () => setSelectedViewerGeometry(viewer.vid, page.id, geomtype, [item.id], true)
                : null,
          }
          if (resizable) {
            hl_props = {
              ...hl_props,
              onLayerInteract,
              z_fac,
              viewerId: viewer.vid,
              page_otid: page.id,
              geomtype,
              itemId: item.id,
            }
            return <RndHighlight {...hl_props} />
          }
          return highlight(hl_props)
        })
      ),
    [geometry, focused_set, selectable, selectedGeometry, z_fac]
  )

  return <div className={clsx(styles.layer, styles.geometryLayer)}>{boxes}</div>
}

const SearchLayerTerms = ({vid, page, thumbs}) => {
  let hits = useFulltextHits({vid, otid: page.id})

  if (thumbs) {
    hits = {}
  }

  let [orgWidth, orgHeight] = page.size
  let w_perc = orgWidth / 100
  let h_perc = orgHeight / 100

  let markterms = useMemo(
    () =>
      flatten(
        mapItems(hits || {}, (term, hits) =>
          hits.map((bbox, i) => {
            // obscure (top, left), (bottom, right) order
            let [x1, y1] = bbox[0]
            let [x2, y2] = bbox[1]
            //~ let enlargeByY = (y2 - y1) * 0.15 // by 15% line height
            //~ y1 = y1 - enlargeByY
            //~ y2 = y2 + enlargeByY

            return highlight({
              x1,
              y1,
              x2,
              y2,
              term,
              w_perc,
              h_perc,
              key: `${term}_${i}`,
              className: styles.searchterm,
            })
          })
        )
      ),

    [hits]
  )

  return (
    <div className={clsx(styles.layer, styles.searchLayer, thumbs && styles.thumbs)}>
      {markterms}
    </div>
  )
}

const SearchLayer = ({page, thumbs}) => {
  let viewer = useViewer()

  let searchData = useSelector(root => root.data.viewer.activeInBookSearches[viewer.vid])
  let isPageHit = searchData ? searchData.pageById[page.id] : false
  if (!isPageHit) {
    return null
  } else {
    return <SearchLayerTerms vid={viewer.vid} page={page} thumbs={thumbs} />
  }
}

export const Layers = {
  words: {
    //~ init: () => {},
    enabled: ({area, zoom, opts}) => !opts?.minZoom || zoom > opts.minZoom,
    render: ({area, zoom}) => <WordLayer key="words" page={area.page} />,
  },
  geometry: {
    //~ init: () => {},
    enabled: ({area, zoom, opts}) => !opts?.minZoom || zoom > opts.minZoom,
    render: ({area, zoom, opts, onLayerInteract}) => (
      // depends on area (instead of area.page) info for resizable support
      // if react-rnd would support percentages in position (instead only in size)
      // we could use the simpler/faster area.page (no mutation on zooming)
      // see https://github.com/bokuweb/react-rnd/issues/611
      <GeometryLayer
        key="polygons"
        z_fac={opts.resizable ? area.orgWidth / area.width : null}
        page={area.page}
        selectable={opts.selectable}
        resizable={opts.resizable}
        onLayerInteract={onLayerInteract}
      />
    ),
  },
  search: {
    //~ init: () => {},
    render: ({area, zoom, opts}) => (
      <SearchLayer thumbs={zoom <= opts.thumbZoom} key={'search'} page={area.page} />
    ),
  },
}
